/**
 * Matterport Interactive Menus (Feb 2026)
 * - MatterportMenusPanel: CRUD editor for viewer-overlay menus.
 * - MatterportMenuOverlay: runtime glass-morphism overlay rendered on top
 *   of the Matterport viewer. Clicking an item navigates to the linked
 *   Mattertag via the Matterport SDK.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ListChecks,
  Plus,
  Trash2,
  Pencil,
  Eye,
  Palette,
  ChevronRight,
  X,
  Menu as MenuIcon,
  Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Presets

const THEME_PRESETS = {
  dark: {
    label: "Dark",
    bg_color: "#0a0a0a",
    bg_opacity: 0.78,
    text_color: "#ffffff",
    accent_color: "#d4af37",
    font_family: "serif",
  },
  light: {
    label: "Light",
    bg_color: "#f7f5ef",
    bg_opacity: 0.9,
    text_color: "#1a1a1a",
    accent_color: "#8a6d1c",
    font_family: "serif",
  },
  custom: {
    label: "Custom",
    bg_color: "#0a0a0a",
    bg_opacity: 0.75,
    text_color: "#ffffff",
    accent_color: "#00BFFF",
    font_family: "sans",
  },
};

const FONT_FAMILY_STYLE = {
  serif: "'Playfair Display', Georgia, serif",
  sans: "'Inter', system-ui, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

const POSITIONS = [
  { id: "top-left", label: "Alto sinistra" },
  { id: "top-right", label: "Alto destra" },
  { id: "bottom-left", label: "Basso sinistra" },
  { id: "bottom-right", label: "Basso destra" },
];

const hexToRgba = (hex, opacity) => {
  const h = (hex || "#000000").replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, Number(opacity) || 0))})`;
};

const emptyMenu = () => ({
  titolo: "",
  sottotitolo: "",
  posizione: "top-left",
  default_visible: false,
  tema: { ...THEME_PRESETS.dark },
  gruppi: [],
});

// ---------------------------------------------------------------------------
// Runtime overlay (rendered above the viewer)

export function MatterportMenuOverlay({ menu, visible, onToggleVisible, onNavigate }) {
  if (!menu) return null;
  const tema = menu.tema || THEME_PRESETS.dark;
  const items = (menu.gruppi || []).slice(0, 6);
  const pos = menu.posizione || "top-left";

  const containerPos = {
    "top-left": "top-4 left-4",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "bottom-right": "bottom-4 right-4",
  }[pos];

  const togglePos = {
    "top-left": "top-4 left-4",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "bottom-right": "bottom-4 right-4",
  }[pos];

  return (
    <>
      {/* Toggle button (always visible so user can re-open) */}
      {!visible && (
        <button
          type="button"
          onClick={onToggleVisible}
          data-testid="matterport-menu-open-btn"
          className={`absolute ${togglePos} z-30 rounded-full shadow-lg backdrop-blur-md hover:scale-105 transition-transform`}
          style={{
            background: hexToRgba(tema.bg_color, tema.bg_opacity),
            color: tema.text_color,
            border: `1px solid ${hexToRgba(tema.accent_color, 0.6)}`,
            padding: "10px 14px",
          }}
          title={menu.titolo || "Apri menu"}
        >
          <div className="flex items-center gap-2">
            <MenuIcon size={16} />
            <span className="text-sm font-medium" style={{ fontFamily: FONT_FAMILY_STYLE[tema.font_family] || FONT_FAMILY_STYLE.serif }}>
              {menu.titolo || "Menu"}
            </span>
          </div>
        </button>
      )}

      {visible && (
        <div
          data-testid="matterport-menu-overlay"
          className={`absolute ${containerPos} z-30 rounded-2xl shadow-2xl backdrop-blur-xl`}
          style={{
            background: hexToRgba(tema.bg_color, tema.bg_opacity),
            color: tema.text_color,
            border: `1px solid ${hexToRgba(tema.accent_color, 0.35)}`,
            minWidth: 260,
            maxWidth: 320,
            fontFamily: FONT_FAMILY_STYLE[tema.font_family] || FONT_FAMILY_STYLE.serif,
          }}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-5 pt-4 pb-3" style={{ borderBottom: `1px solid ${hexToRgba(tema.accent_color, 0.3)}` }}>
            <div className="pr-2">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: tema.accent_color }}>
                Percorso
              </div>
              <div className="text-lg font-semibold leading-snug mt-0.5" style={{ color: tema.text_color }}>
                {menu.titolo}
              </div>
              {menu.sottotitolo && (
                <div className="text-xs opacity-80 mt-0.5" style={{ color: tema.text_color }}>
                  {menu.sottotitolo}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onToggleVisible}
              data-testid="matterport-menu-close-btn"
              className="rounded-full p-1 hover:opacity-80 transition-opacity"
              style={{ color: tema.text_color }}
              title="Nascondi menu"
            >
              <X size={16} />
            </button>
          </div>

          {/* Items */}
          <ul className="py-2">
            {items.length === 0 && (
              <li className="px-5 py-3 text-sm opacity-70">Nessuna voce configurata</li>
            )}
            {items.map((g, idx) => (
              <li key={g.id || idx}>
                <button
                  type="button"
                  onClick={() => g.tag_id && onNavigate?.(g.tag_id)}
                  disabled={!g.tag_id}
                  data-testid={`matterport-menu-item-${idx}`}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-white/10 transition-colors disabled:opacity-40"
                >
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold"
                    style={{
                      background: hexToRgba(tema.accent_color, 0.18),
                      color: tema.accent_color,
                      border: `1px solid ${hexToRgba(tema.accent_color, 0.5)}`,
                    }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate" style={{ color: tema.text_color }}>
                      {g.label}
                    </span>
                    {g.descrizione && (
                      <span className="block text-[11px] opacity-70 truncate" style={{ color: tema.text_color }}>
                        {g.descrizione}
                      </span>
                    )}
                  </span>
                  <ChevronRight size={14} style={{ color: tema.accent_color }} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Editor dialog

function MenuEditorDialog({ open, onOpenChange, initial, onSave, pois }) {
  const [form, setForm] = useState(() => initial ? { ...emptyMenu(), ...initial } : emptyMenu());
  const [presetKey, setPresetKey] = useState("dark");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = initial ? { ...emptyMenu(), ...initial } : emptyMenu();
      // Guess preset from bg/text colors
      let guessed = "custom";
      for (const [k, v] of Object.entries(THEME_PRESETS)) {
        if (v.bg_color === base?.tema?.bg_color && v.text_color === base?.tema?.text_color) {
          guessed = k;
          break;
        }
      }
      setPresetKey(guessed);
      setForm(base);
    }
  }, [open, initial]);

  const applyPreset = (key) => {
    setPresetKey(key);
    setForm((f) => ({ ...f, tema: { ...THEME_PRESETS[key], ...(key === "custom" ? f.tema : {}) } }));
    if (key !== "custom") {
      setForm((f) => ({ ...f, tema: { ...THEME_PRESETS[key] } }));
    }
  };

  const updateGroup = (idx, patch) => {
    setForm((f) => {
      const gruppi = [...(f.gruppi || [])];
      gruppi[idx] = { ...gruppi[idx], ...patch };
      return { ...f, gruppi };
    });
  };
  const addGroup = () => {
    setForm((f) => {
      if ((f.gruppi || []).length >= 6) return f;
      return { ...f, gruppi: [...(f.gruppi || []), { label: "", tag_id: "", descrizione: "" }] };
    });
  };
  const removeGroup = (idx) => {
    setForm((f) => ({ ...f, gruppi: (f.gruppi || []).filter((_, i) => i !== idx) }));
  };
  const autoFillFromPois = () => {
    // Riempie le voci con i primi POI disponibili (fino a 6) usando titolo/descrizione in italiano.
    const seen = new Set();
    const opts = [];
    for (const p of pois || []) {
      if (!p.matterport_tag_id || seen.has(p.matterport_tag_id)) continue;
      seen.add(p.matterport_tag_id);
      const it = (p.translations || []).find((t) => t.language === "it") || p.translations?.[0] || {};
      opts.push({
        tag_id: p.matterport_tag_id,
        label: (it.title || `POI ${(p.id || "").slice(0, 6)}`).slice(0, 80),
        descrizione: (it.description || "").slice(0, 200),
      });
      if (opts.length >= 6) break;
    }
    if (opts.length === 0) {
      toast.error("Nessun POI collegato a un Mattertag");
      return;
    }
    setForm((f) => ({ ...f, gruppi: opts }));
    toast.success(`Importate ${opts.length} voci dai POI`);
  };

  const tagOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of pois || []) {
      if (!p.matterport_tag_id || seen.has(p.matterport_tag_id)) continue;
      seen.add(p.matterport_tag_id);
      const it = (p.translations || []).find((t) => t.language === "it");
      const label = it?.title || p.translations?.[0]?.title || `POI ${(p.id || "").slice(0, 6)}`;
      const desc = it?.description || p.translations?.[0]?.description || "";
      out.push({ id: p.matterport_tag_id, label, desc });
    }
    return out;
  }, [pois]);

  const canSave = form.titolo?.trim() && (form.gruppi || []).length >= 4 && (form.gruppi || []).length <= 6
    && form.gruppi.every((g) => g.label?.trim() && g.tag_id?.trim());

  const handleSave = async () => {
    if (!canSave) {
      toast.error("Servono 4–6 voci con etichetta e tag");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const tema = form.tema || THEME_PRESETS.dark;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!fixed !left-1/2 !top-6 !-translate-x-1/2 !translate-y-0 !max-w-2xl w-[92vw] bg-slate-900/95 border-slate-700 text-slate-100 backdrop-blur-xl shadow-2xl rounded-2xl p-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="menu-editor-dialog"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-800">
          <DialogTitle className="text-lg">{initial?.id ? "Modifica Menu" : "Nuovo Menu Interattivo"}</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Crea 4–6 voci scegliendole dai POI del modello. Il menu comparirà automaticamente sopra la vista 3D.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Riga 1: Titolo + Sottotitolo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Titolo *</Label>
              <Input
                data-testid="menu-editor-title"
                value={form.titolo}
                onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                placeholder="Es. Percorso Villa"
                className="bg-slate-800 border-slate-700 h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Sottotitolo</Label>
              <Input
                data-testid="menu-editor-subtitle"
                value={form.sottotitolo}
                onChange={(e) => setForm({ ...form, sottotitolo: e.target.value })}
                placeholder="Opzionale"
                className="bg-slate-800 border-slate-700 h-9"
              />
            </div>
          </div>

          {/* Riga 2: Posizione + Default visible */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs">Posizione overlay</Label>
              <Select value={form.posizione} onValueChange={(v) => setForm({ ...form, posizione: v })}>
                <SelectTrigger data-testid="menu-editor-position" className="bg-slate-800 border-slate-700 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch
                data-testid="menu-editor-default-visible"
                checked={!!form.default_visible}
                onCheckedChange={(v) => setForm({ ...form, default_visible: v })}
              />
              <Label className="cursor-pointer text-xs" onClick={() => setForm({ ...form, default_visible: !form.default_visible })}>
                Aperto all&apos;avvio del modello
              </Label>
            </div>
          </div>

          {/* Tema compatto */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette size={13} className="text-slate-400" />
                <span className="text-xs font-medium">Tema</span>
              </div>
              <div className="flex items-center gap-1">
                {Object.entries(THEME_PRESETS).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => applyPreset(k)}
                    data-testid={`menu-editor-preset-${k}`}
                    className={`text-[11px] py-1 px-2 rounded border transition-colors ${
                      presetKey === k ? "border-cyan-400 bg-cyan-500/10 text-cyan-200" : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div>
                <Label className="text-[10px] text-slate-400">Sfondo</Label>
                <input
                  type="color"
                  value={tema.bg_color}
                  onChange={(e) => { setForm({ ...form, tema: { ...tema, bg_color: e.target.value } }); setPresetKey("custom"); }}
                  className="w-full h-7 bg-slate-800 border border-slate-700 rounded cursor-pointer"
                  data-testid="menu-editor-bg-color"
                />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">Testo</Label>
                <input
                  type="color"
                  value={tema.text_color}
                  onChange={(e) => { setForm({ ...form, tema: { ...tema, text_color: e.target.value } }); setPresetKey("custom"); }}
                  className="w-full h-7 bg-slate-800 border border-slate-700 rounded cursor-pointer"
                  data-testid="menu-editor-text-color"
                />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">Accento</Label>
                <input
                  type="color"
                  value={tema.accent_color}
                  onChange={(e) => { setForm({ ...form, tema: { ...tema, accent_color: e.target.value } }); setPresetKey("custom"); }}
                  className="w-full h-7 bg-slate-800 border border-slate-700 rounded cursor-pointer"
                  data-testid="menu-editor-accent-color"
                />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">Opacità</Label>
                <input
                  type="range"
                  min={0} max={1} step={0.05}
                  value={tema.bg_opacity ?? 0.75}
                  onChange={(e) => { setForm({ ...form, tema: { ...tema, bg_opacity: parseFloat(e.target.value) } }); setPresetKey("custom"); }}
                  className="w-full mt-1"
                  data-testid="menu-editor-bg-opacity"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-slate-400">Font</Label>
              <Select
                value={tema.font_family || "serif"}
                onValueChange={(v) => { setForm({ ...form, tema: { ...tema, font_family: v } }); setPresetKey("custom"); }}
              >
                <SelectTrigger data-testid="menu-editor-font" className="bg-slate-800 border-slate-700 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="serif">Serif (elegante)</SelectItem>
                  <SelectItem value="sans">Sans-serif</SelectItem>
                  <SelectItem value="mono">Monospace</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Voci */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Voci ({(form.gruppi || []).length}/6)</Label>
              <div className="flex items-center gap-1">
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={autoFillFromPois}
                  data-testid="menu-editor-autofill-pois"
                  className="h-7 border-slate-700 text-xs"
                  title="Riempi automaticamente le voci con i POI esistenti"
                >
                  Importa da POI
                </Button>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={addGroup}
                  disabled={(form.gruppi || []).length >= 6}
                  data-testid="menu-editor-add-group"
                  className="h-7 border-slate-700 text-xs"
                >
                  <Plus size={12} className="mr-1" /> Voce
                </Button>
              </div>
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {(form.gruppi || []).length === 0 && (
                <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-700 rounded">
                  Aggiungi almeno 4 voci (max 6). Clicca <b>Importa da POI</b> per riempirle in un click.
                </div>
              )}
              {(form.gruppi || []).map((g, idx) => (
                <div key={g.id || idx} className="p-2 rounded border border-slate-700 bg-slate-800/60 flex items-center gap-2">
                  <Badge variant="outline" className="border-cyan-500/50 text-cyan-300 shrink-0">{String(idx + 1).padStart(2, "0")}</Badge>
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Select
                      value={g.tag_id || ""}
                      onValueChange={(v) => {
                        const chosen = tagOptions.find((t) => t.id === v);
                        setForm((f) => {
                          const gruppi = [...(f.gruppi || [])];
                          const prev = gruppi[idx] || {};
                          gruppi[idx] = {
                            ...prev,
                            tag_id: v,
                            label: prev.label?.trim() ? prev.label : (chosen?.label || ""),
                            descrizione: prev.descrizione?.trim() ? prev.descrizione : (chosen?.desc || ""),
                          };
                          return { ...f, gruppi };
                        });
                      }}
                    >
                      <SelectTrigger data-testid={`menu-editor-group-tag-${idx}`} className="bg-slate-900 border-slate-700 h-8 text-xs">
                        <SelectValue placeholder="Seleziona POI" />
                      </SelectTrigger>
                      <SelectContent>
                        {tagOptions.length === 0 && (
                          <SelectItem value="__none__" disabled>Nessun POI disponibile</SelectItem>
                        )}
                        {tagOptions.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={g.label || ""}
                      onChange={(e) => updateGroup(idx, { label: e.target.value })}
                      placeholder="Etichetta"
                      className="bg-slate-900 border-slate-700 h-8 text-xs"
                      data-testid={`menu-editor-group-label-${idx}`}
                    />
                  </div>
                  <Button
                    type="button" size="icon" variant="ghost"
                    onClick={() => removeGroup(idx)}
                    data-testid={`menu-editor-group-remove-${idx}`}
                    className="h-8 w-8 text-rose-400 shrink-0"
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Anteprima inline */}
          <div>
            <Label className="text-xs">Anteprima</Label>
            <div className="relative h-[180px] mt-1 rounded-lg overflow-hidden border border-slate-700 bg-[url('https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=800')] bg-cover bg-center">
              <div className="absolute inset-0 bg-black/40" />
              <MatterportMenuOverlay
                menu={form}
                visible={true}
                onToggleVisible={() => {}}
                onNavigate={() => toast.info("Anteprima")}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-800 bg-slate-900/80">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="menu-editor-cancel">
            Annulla
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving}
            data-testid="menu-editor-save"
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
            Salva e mostra sul modello
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Panel (sidebar CRUD)

export function MatterportMenusPanel({
  authToken,
  pois,
  onPublishedChange,
}) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // menu obj or null

  const fetchMenus = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/matterport-menus?token=${authToken}`);
      const list = Array.isArray(data) ? data : [];
      setMenus(list);
      const pub = list.find((m) => m.published);
      onPublishedChange?.(pub || null);
    } catch (e) {
      toast.error("Errore caricamento menu");
    } finally {
      setLoading(false);
    }
  }, [authToken, onPublishedChange]);

  useEffect(() => { fetchMenus(); }, [fetchMenus]);

  const handleSave = async (form) => {
    try {
      if (editing?.id) {
        // Modifica: manteniamo il menu attivo (published=true) sull'update
        const { data } = await axios.put(
          `${API_URL}/api/matterport-menus/${editing.id}?token=${authToken}`,
          { ...form, published: true },
        );
        setMenus((ms) => ms.map((m) => (m.id === data.id ? data : { ...m, published: false })));
        onPublishedChange?.(data);
        toast.success("Menu aggiornato e mostrato sopra il modello");
      } else {
        // Nuovo menu: il backend lo pubblica automaticamente
        const { data } = await axios.post(
          `${API_URL}/api/matterport-menus?token=${authToken}`,
          form,
        );
        setMenus((ms) => [data, ...ms.map((m) => ({ ...m, published: false }))]);
        onPublishedChange?.(data);
        toast.success("Menu creato e mostrato sopra il modello");
      }
    } catch (e) {
      toast.error("Salvataggio fallito: " + (e.response?.data?.detail || e.message));
      throw e;
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Eliminare "${m.titolo}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/matterport-menus/${m.id}?token=${authToken}`);
      setMenus((ms) => ms.filter((x) => x.id !== m.id));
      if (m.published) onPublishedChange?.(null);
      toast.success("Menu eliminato");
    } catch (e) {
      toast.error("Eliminazione fallita");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-200">
          <ListChecks size={14} />
          <span className="text-sm font-medium">Menu Interattivi</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 border-slate-700 text-xs"
          onClick={() => { setEditing(null); setEditorOpen(true); }}
          data-testid="matterport-menus-new-btn"
        >
          <Plus size={12} className="mr-1" /> Nuovo
        </Button>
      </div>

      <div className="text-[11px] text-slate-500 leading-snug">
        Il menu appena creato compare automaticamente sopra il modello 3D. Solo un menu attivo alla volta.
      </div>

      {loading && <div className="text-xs text-slate-500 py-2">Caricamento…</div>}

      {!loading && menus.length === 0 && (
        <div className="text-xs text-slate-500 py-3 text-center border border-dashed border-slate-700 rounded">
          Nessun menu configurato
        </div>
      )}

      <div className="space-y-1.5" data-testid="matterport-menus-list">
        {menus.map((m) => {
          const active = !!m.published;
          return (
            <div
              key={m.id}
              className={`p-2 rounded border ${active ? "border-emerald-500 bg-emerald-500/10" : "border-slate-700 bg-slate-800/40"}`}
              data-testid={`matterport-menu-row-${m.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white truncate flex items-center gap-1.5">
                    {m.titolo}
                    {active && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 border border-emerald-400 text-emerald-300"
                        data-testid={`matterport-menu-status-${m.id}`}
                      >
                        In vetrina
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {(m.gruppi || []).length} voci · {m.posizione}
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  {!active && (
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7 text-cyan-300"
                      onClick={async () => {
                        try {
                          const { data } = await axios.post(
                            `${API_URL}/api/matterport-menus/${m.id}/publish?token=${authToken}`,
                          );
                          setMenus((ms) => ms.map((x) => x.id === data.id ? data : { ...x, published: false }));
                          onPublishedChange?.(data);
                          toast.success(`"${data.titolo}" in vetrina`);
                        } catch (e) {
                          toast.error("Errore: " + (e.response?.data?.detail || e.message));
                        }
                      }}
                      data-testid={`matterport-menu-activate-${m.id}`}
                      title="Mostra questo menu sul modello 3D"
                    >
                      <Eye size={14} />
                    </Button>
                  )}
                  <Button
                    size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => { setEditing(m); setEditorOpen(true); }}
                    data-testid={`matterport-menu-edit-${m.id}`}
                    title="Modifica"
                  >
                    <Pencil size={13} />
                  </Button>
                  <Button
                    size="icon" variant="ghost" className="h-7 w-7 text-rose-400"
                    onClick={() => handleDelete(m)}
                    data-testid={`matterport-menu-delete-${m.id}`}
                    title="Elimina"
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <MenuEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        onSave={handleSave}
        pois={pois}
      />
    </div>
  );
}
